import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { QRCodeCanvas } from 'qrcode.react';
import { X, Download } from 'lucide-react';

export default function ShareCardModal({ isOpen, onClose, eventData }) {
  const cardRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen || !eventData) return null;

  const eventUrl = `${window.location.origin}/app/events/${eventData.id}`;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3, // High resolution for IG Stories
        useCORS: true, // Allow cross-origin images (important for S3/Supabase avatars)
        backgroundColor: null
      });
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `joinu-share-${eventData.id}.png`;
      link.click();
    } catch (error) {
      console.error('Failed to generate image', error);
      alert('產生圖片失敗，請稍後再試');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', padding: '20px' }}>
      <div style={{ position: 'relative', width: '100%', maxWidth: '360px', background: '#ffffff', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>分享到 IG 限動</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: '#64748b' }}>
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        {/* The Card to be captured (9:16 aspect ratio roughly = 270x480) */}
        <div style={{ padding: '20px', background: '#f8fafc', display: 'flex', justifyContent: 'center' }}>
          <div
            ref={cardRef}
            style={{
              width: '270px',
              height: '480px',
              background: 'linear-gradient(135deg, #4e8bca 0%, #6a9fd3 100%)',
              borderRadius: '24px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 12px 24px rgba(78, 139, 202, 0.3)'
            }}
          >
            {/* Logo/Brand */}
            <div style={{ color: 'white', fontSize: '22px', fontWeight: '900', fontStyle: 'italic', marginBottom: '16px', textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              JoinU
            </div>

            {/* Event Image */}
            {eventData.image_url ? (
              <div style={{ width: '100%', height: '160px', borderRadius: '16px', overflow: 'hidden', marginBottom: '16px', boxShadow: '0 8px 16px rgba(0,0,0,0.15)', background: '#cbd5e1' }}>
                <img src={eventData.image_url} alt="Event" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ) : (
              <div style={{ width: '100%', height: '160px', borderRadius: '16px', marginBottom: '16px', background: 'linear-gradient(to bottom right, #f1f5f9, #e2e8f0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '40px' }}>🏃</span>
              </div>
            )}

            {/* Event Info */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#376ba1', fontWeight: '800', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {eventData.category}
                </div>
                <div style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', lineHeight: '1.35', marginBottom: '8px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {eventData.title}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                  📍 {eventData.city || '台灣'}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #e2e8f0' }}>
                <div style={{ fontSize: '12px', color: '#0f172a', fontWeight: '800' }}>
                  掃碼看活動 👉
                </div>
                <div style={{ padding: '4px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <QRCodeCanvas value={eventUrl} size={50} level="L" marginSize={1} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div style={{ padding: '16px 20px', display: 'flex', gap: '12px' }}>
          <button
            onClick={handleDownload}
            disabled={isGenerating}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: 'white', border: 'none', borderRadius: '14px', fontSize: '15px', fontWeight: '700', cursor: isGenerating ? 'wait' : 'pointer', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)' }}
          >
            {isGenerating ? '產生中...' : <><Download size={18} /> 下載限動圖片</>}
          </button>
        </div>

      </div>
    </div>
  );
}
